import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { getAuth } from 'firebase/auth';
import { MessageCircle } from '@styled-icons/feather/MessageCircle';
import { Heart } from '@styled-icons/feather/Heart';
import { Users } from '@styled-icons/feather/Users';
import { Lock } from '@styled-icons/feather/Lock';
import { device } from '../../constants/device-sizes';
import { API_URL } from '../../constants/string-constants';
import { getSiteName } from '../../hooks/useSiteConfig';
import {
  ModalContent,
  ModernTitle,
  ModernSubtitle,
} from './shared/ModalBase';

const WideModalWrapper = styled.div`
  width: 95%;
  max-width: 700px;
  padding: 1rem;
  max-height: 90vh;
  overflow-y: auto;
  @media ${device.tablet} {
    width: 85%;
    max-width: 1000px;
    padding: 1.5rem;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin: 2rem 0;
`;

const IntroText = styled.p`
  color: var(--clr-sec);
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 1.5rem 0;
  opacity: 0.9;

  @media ${device.tablet} {
    font-size: 1.2rem;
  }
`;

const ChoiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 2rem 0;

  @media ${device.tablet} {
    flex-direction: row;
    justify-content: center;
    gap: 2rem;
  }
`;

const ChoiceButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  background: ${(props) => {
    if (props.variant === 'chat') return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    if (props.variant === 'vent') return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
    if (props.variant === 'private') return 'linear-gradient(135deg, #ff6b6b 0%, #ff9a9e 100%)';
    return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  }};
  border: none;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  min-height: 150px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  flex: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover:not(:disabled) {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);

    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media ${device.tablet} {
    min-width: 250px;
  }
`;

const ChoiceIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;

  svg {
    width: 3rem;
    height: 3rem;
  }
`;

const ChoiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ChoiceDescription = styled.p`
  font-size: 0.95rem;
  margin: 0;
  opacity: 0.95;
  line-height: 1.4;
`;

const ActiveUsersIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  color: white;
  opacity: 0.95;

  svg {
    width: 0.9rem;
    height: 0.9rem;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  z-index: 10;
`;

const LoadingText = styled.p`
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  color: #ff8a8a;
  font-size: 0.9rem;
  margin: 1rem 0;
  padding: 0.75rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff6b6b;
  text-align: center;
`;

const HighlightText = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const AgeDisclaimer = styled.p`
  color: var(--clr-sec);
  font-size: 0.9rem;
  text-align: center;
  margin: 1rem 0;
  opacity: 0.8;

  a {
    color: var(--clr-purple);
    text-decoration: none;
    border-bottom: 1px solid var(--clr-purple);
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const OnboardingModalV2 = ({ visible, onClose, onRoomSelect }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rooms, setRooms] = useState([]);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [roomStats, setRoomStats] = useState({ chat: null, vent: null });

  // Reset loading state when modal is closed
  useEffect(() => {
    if (!visible) {
      setLoading(false);
      setSelectedChoice(null);
      setError('');
      setRoomStats({ chat: null, vent: null });
    }
  }, [visible]);

  // Fetch rooms on mount to show stats
  useEffect(() => {
    if (visible && rooms.length === 0) {
      fetchRoomsForStats();
    }
  }, [visible]);

  const fetchRooms = async () => {
    setError('');

    try {
      const browserLanguage =
        navigator.language || navigator.userLanguage || 'en';
      const auth = getAuth();
      const headers = {
        'Content-Type': 'application/json',
      };

      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken();
          headers.Authorization = 'Bearer ' + token;
        } catch (tokenError) {
          console.warn('Failed to get auth token:', tokenError);
        }
      }

      const response = await fetch(
        API_URL + `rooms?language=${encodeURIComponent(browserLanguage)}`,
        {
          method: 'GET',
          headers,
        }
      );

      const result = await response.json();

      if (response.ok) {
        setRooms(result.rooms || []);
        return result.rooms || [];
      } else {
        setError(result.message || 'Failed to load rooms');
        return [];
      }
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError('Network error. Please try again.');
      return [];
    }
  };

  const fetchRoomsForStats = async () => {
    const fetchedRooms = await fetchRooms();
    if (fetchedRooms.length > 0) {
      // Calculate stats for chat rooms
      const chatRooms = fetchedRooms.filter(
        (room) =>
          room.accessible !== false &&
          (room.title.toLowerCase().includes('chat') ||
            room.description.toLowerCase().includes('chat') ||
            room.id.toLowerCase().includes('chat'))
      );

      const ventRooms = fetchedRooms.filter(
        (room) =>
          room.accessible !== false &&
          (room.title.toLowerCase().includes('vent') ||
            room.description.toLowerCase().includes('vent') ||
            room.id.toLowerCase().includes('vent'))
      );

      const chatStats =
        chatRooms.length > 0
          ? chatRooms.reduce((sum, room) => sum + (room.onlineUsers || 0), 0)
          : fetchedRooms
              .filter((r) => r.accessible !== false)
              .reduce((sum, room) => sum + (room.onlineUsers || 0), 0);

      const ventStats =
        ventRooms.length > 0
          ? ventRooms.reduce((sum, room) => sum + (room.onlineUsers || 0), 0)
          : fetchedRooms
              .filter((r) => r.accessible !== false)
              .reduce((sum, room) => sum + (room.onlineUsers || 0), 0);

      setRoomStats({
        chat: chatStats,
        vent: ventStats,
      });
    }
  };

  const handleChoice = async (choice) => {
    setLoading(true);
    setSelectedChoice(choice);

    try {
      const fetchedRooms = await fetchRooms();

      if (fetchedRooms.length === 0) {
        setError('No rooms available. Please try again later.');
        setLoading(false);
        setSelectedChoice(null);
        return;
      }

      let targetRoom = null;

      if (choice === 'chat') {
        const chatRooms = fetchedRooms.filter(
          (room) =>
            room.accessible !== false &&
            (room.title.toLowerCase().includes('chat') ||
              room.description.toLowerCase().includes('chat') ||
              room.id.toLowerCase().includes('chat'))
        );

        if (chatRooms.length > 0) {
          targetRoom = chatRooms.reduce((prev, current) =>
            current.onlineUsers > prev.onlineUsers ? current : prev
          );
        } else {
          const accessibleRooms = fetchedRooms.filter(
            (room) => room.accessible !== false
          );
          if (accessibleRooms.length > 0) {
            targetRoom = accessibleRooms.reduce((prev, current) =>
              current.onlineUsers > prev.onlineUsers ? current : prev
            );
          }
        }
      } else if (choice === 'vent') {
        const ventRooms = fetchedRooms.filter(
          (room) =>
            room.accessible !== false &&
            (room.title.toLowerCase().includes('vent') ||
              room.description.toLowerCase().includes('vent') ||
              room.id.toLowerCase().includes('vent'))
        );

        if (ventRooms.length > 0) {
          targetRoom = ventRooms.reduce((prev, current) =>
            current.onlineUsers > prev.onlineUsers ? current : prev
          );
        } else {
          const accessibleRooms = fetchedRooms.filter(
            (room) => room.accessible !== false
          );
          if (accessibleRooms.length > 0) {
            targetRoom = accessibleRooms.reduce((prev, current) =>
              current.onlineUsers > prev.onlineUsers ? current : prev
            );
          }
        }
      } else if (choice === 'private') {
        // Handle private rage room - redirect to /rage in same tab
        window.location.href = '/rage';
        return;
      }

      if (targetRoom) {
        // Don't close the modal here - let the parent handle it
        // This keeps the loading state visible until the room is actually selected
        onRoomSelect(targetRoom);
        // Keep loading state active - parent will close the modal
      } else {
        setError(
          'No accessible rooms available. Please check your profile settings.'
        );
        setLoading(false);
        setSelectedChoice(null);
      }
    } catch (err) {
      console.error('Error selecting room:', err);
      setError('Failed to join room. Please try again.');
      setLoading(false);
      setSelectedChoice(null);
    }
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={() => {}}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <WideModalWrapper>
        <ModalContent>
          <WelcomeSection>
            <ModernTitle>
              Welcome to <HighlightText>{getSiteName()}</HighlightText>
            </ModernTitle>
            <IntroText>
              Chill, anonymous chat rooms where you can express yourself
              freely. Whether you need to vent about life's challenges or just
              want to hang out and chat with others, we're here for you.
            </IntroText>
            <ModernSubtitle>What brings you here today?</ModernSubtitle>
          </WelcomeSection>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <AgeDisclaimer>
            You must be 16+ to continue. By proceeding, you confirm you meet this age requirement and agree to our{' '}
            <a href="/rules" target="_blank" rel="noopener noreferrer">
              community rules
            </a>.
          </AgeDisclaimer>

          <ChoiceContainer>
            <ChoiceButton
              variant='chat'
              onClick={() => handleChoice('chat')}
              disabled={loading}
            >
              {loading && selectedChoice === 'chat' && (
                <LoadingOverlay>
                  <LoadingText>Finding the best room...</LoadingText>
                </LoadingOverlay>
              )}
              <ChoiceIcon>
                <MessageCircle />
              </ChoiceIcon>
              <ChoiceTitle>Chat with others</ChoiceTitle>
              <ChoiceDescription>
                Connect with others in casual conversation
              </ChoiceDescription>
              {roomStats.chat !== null && (
                <ActiveUsersIndicator>
                  <Users />
                  {roomStats.chat} {roomStats.chat === 1 ? 'person' : 'people'}{' '}
                  online
                </ActiveUsersIndicator>
              )}
            </ChoiceButton>

            <ChoiceButton
              variant='vent'
              onClick={() => handleChoice('vent')}
              disabled={loading}
            >
              {loading && selectedChoice === 'vent' && (
                <LoadingOverlay>
                  <LoadingText>Finding the best room...</LoadingText>
                </LoadingOverlay>
              )}
              <ChoiceIcon>
                <Heart />
              </ChoiceIcon>
              <ChoiceTitle>Vent with others</ChoiceTitle>
              <ChoiceDescription>
                Share what's on your mind in a supportive space
              </ChoiceDescription>
              {roomStats.vent !== null && (
                <ActiveUsersIndicator>
                  <Users />
                  {roomStats.vent} {roomStats.vent === 1 ? 'person' : 'people'}{' '}
                  online
                </ActiveUsersIndicator>
              )}
            </ChoiceButton>

            <ChoiceButton
              variant='private'
              onClick={() => handleChoice('private')}
              disabled={loading}
            >
              {loading && selectedChoice === 'private' && (
                <LoadingOverlay>
                  <LoadingText>Opening private space...</LoadingText>
                </LoadingOverlay>
              )}
              <ChoiceIcon>
                <Lock />
              </ChoiceIcon>
              <ChoiceTitle>Vent privately</ChoiceTitle>
              <ChoiceDescription>
                A private, solo venting room
              </ChoiceDescription>
              <ActiveUsersIndicator>
                <Lock />
                Just you
              </ActiveUsersIndicator>
            </ChoiceButton>
          </ChoiceContainer>
        </ModalContent>
      </WideModalWrapper>
    </Modal>
  );
};

export default OnboardingModalV2;
